import React from 'react';
import { Artifact, ArtifactType } from '../../types/artifact';
import { TextArtifactViewer } from './viewers/TextArtifactViewer';
import { CodeArtifactViewer } from './viewers/CodeArtifactViewer';
import { ImageArtifactViewer } from './viewers/ImageArtifactViewer';
import { IdeaArtifactViewer } from './viewers/IdeaArtifactViewer';
import { CritiqueArtifactViewer } from './viewers/CritiqueArtifactViewer';
import { SolutionArtifactViewer } from './viewers/SolutionArtifactViewer';
import { DocumentArtifactViewer } from './viewers/DocumentArtifactViewer';
import { DiagramArtifactViewer } from './viewers/DiagramArtifactViewer';
import { MermaidDiagramViewer } from './viewers/MermaidDiagramViewer';
import { PersonaChatArtifactViewer } from './viewers/PersonaChatArtifactViewer';
import { MapArtifactViewer } from './viewers/MapArtifactViewer';

// Trailmap L2 (spec §6.1): expanded tiles render artifact bodies WITHOUT
// the chrome (header, metadata block, copy button, viewMode toggle) that
// ArtifactViewer wraps the body in. ArtifactTileBody is the body-only
// renderer that the canvas tile mounts directly. The viewMode-driven
// switch is preserved so the same body works for ArtifactViewer's chrome
// path AND in-tile expansion (Phase 5).
//
// headingShift threads down to sub-viewers so user-authored `# Heading`
// markdown rebases below the tile's outer-button h3 (a11y F3: prevents
// page outline pollution by N competing h1s when N markdown tiles
// are expanded simultaneously, WCAG 1.3.1).

export type TileViewMode =
  | 'default'
  | 'preview'
  | 'editor'
  | 'formatted'
  | 'raw'
  | 'interactive'
  | 'static';

export interface ArtifactTileBodyProps {
  artifact: Artifact;
  viewMode?: TileViewMode;
  headingShift?: number;
}

export const ArtifactTileBody: React.FC<ArtifactTileBodyProps> = ({
  artifact,
  viewMode = 'default',
  headingShift = 0,
}) => {
  return (
    <div className="artifact-tile-body" data-artifact-type={artifact.type}>
      {renderBody(artifact, viewMode, headingShift)}
    </div>
  );
};

function renderBody(
  artifact: Artifact,
  viewMode: TileViewMode,
  headingShift: number,
): React.ReactNode {
  // 1. Explicit viewMode override for types that support modes.
  if (viewMode !== 'default') {
    switch (artifact.type) {
      case ArtifactType.CODE:
        if (viewMode === 'editor' || viewMode === 'preview') {
          return <CodeArtifactViewer artifact={artifact} mode={viewMode} />;
        }
        break;
      case ArtifactType.DOCUMENT:
        if (viewMode === 'formatted' || viewMode === 'raw') {
          return (
            <DocumentArtifactViewer
              artifact={artifact}
              mode={viewMode}
              headingShift={headingShift}
            />
          );
        }
        break;
      case ArtifactType.DIAGRAM:
        if (viewMode === 'interactive' || viewMode === 'static') {
          return <DiagramArtifactViewer artifact={artifact} mode={viewMode} />;
        }
        break;
    }
  }

  // 2. metadata.format overrides type-based routing (e.g. type="document"
  // semantic role + format="yaml" rendering preference).
  const format = artifact.metadata?.format as string | undefined;
  if (format) {
    switch (format) {
      case 'json':
        return (
          <CodeArtifactViewer
            artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'json' } }}
          />
        );
      case 'yaml':
      case 'yml':
        return (
          <CodeArtifactViewer
            artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'yaml' } }}
          />
        );
      case 'toml':
        return (
          <CodeArtifactViewer
            artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'toml' } }}
          />
        );
      case 'csv':
      case 'tsv':
        return (
          <CodeArtifactViewer
            artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'plaintext' } }}
          />
        );
      case 'prompt':
        return (
          <CodeArtifactViewer
            artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'markdown' } }}
          />
        );
      case 'mermaid':
        return <MermaidDiagramViewer artifact={artifact} />;
      case 'code':
        return <CodeArtifactViewer artifact={artifact} />;
      case 'image':
        return <ImageArtifactViewer artifact={artifact} />;
      case 'markdown':
      case 'text':
        return <TextArtifactViewer artifact={artifact} headingShift={headingShift} />;
      // unknown format — fall through to type-based routing below
    }
  }

  // 3. Type-based fallback. Mirrors ArtifactViewer.renderViewer; the
  // typed-backend list (decision_log et al.) routes through
  // DocumentArtifactViewer per the same smart-fallback policy.
  switch (artifact.type) {
    case ArtifactType.TEXT:
    case ArtifactType.MARKDOWN:
      return <TextArtifactViewer artifact={artifact} headingShift={headingShift} />;
    case ArtifactType.CODE:
      return <CodeArtifactViewer artifact={artifact} />;
    case ArtifactType.IMAGE:
      return <ImageArtifactViewer artifact={artifact} />;
    case ArtifactType.IDEA:
      return <IdeaArtifactViewer artifact={artifact} headingShift={headingShift} />;
    case ArtifactType.CRITIQUE:
      return <CritiqueArtifactViewer artifact={artifact} headingShift={headingShift} />;
    case ArtifactType.SOLUTION:
      return <SolutionArtifactViewer artifact={artifact} headingShift={headingShift} />;
    case ArtifactType.DOCUMENT:
      return <DocumentArtifactViewer artifact={artifact} headingShift={headingShift} />;
    case ArtifactType.DIAGRAM:
      return <DiagramArtifactViewer artifact={artifact} />;
    case ArtifactType.JSON:
      return (
        <CodeArtifactViewer
          artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'json' } }}
        />
      );
    case ArtifactType.YAML:
      return (
        <CodeArtifactViewer
          artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'yaml' } }}
        />
      );
    case ArtifactType.TOML:
      return (
        <CodeArtifactViewer
          artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'toml' } }}
        />
      );
    case ArtifactType.CSV:
      return (
        <CodeArtifactViewer
          artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'plaintext' } }}
        />
      );
    case ArtifactType.PROMPT:
      return (
        <CodeArtifactViewer
          artifact={{ ...artifact, metadata: { ...artifact.metadata, language: 'markdown' } }}
        />
      );
    case ArtifactType.PERSONA_CHAT:
      return <PersonaChatArtifactViewer artifact={artifact} />;
    case ArtifactType.GEOJSON:
      return <MapArtifactViewer artifact={artifact} />;
    case ArtifactType.MERMAID:
      // Backend emits `type: "mermaid"` for create_artifacts calls with
      // mermaid_code payloads. Without an explicit case the dispatch
      // fell to the smart-fallback default → DocumentArtifactViewer
      // dumped the JSON content as text instead of rendering. See
      // backend/app/schemas/artifact.py for the canonical enum.
      return <MermaidDiagramViewer artifact={artifact} />;
    case ArtifactType.DECISION_LOG:
    case ArtifactType.PULSE_DIGEST:
    case ArtifactType.HANDOFF_NOTE:
    case ArtifactType.MINDMAP:
    case ArtifactType.PROJECT_MANIFEST:
      return <DocumentArtifactViewer artifact={artifact} headingShift={headingShift} />;
    default:
      // Smart fallback — DocumentArtifactViewer's renderFormatted
      // handles {markdown}/{html}/{text}/{body}/{content} object shapes
      // gracefully via extractMarkdownBody. Future-proofs new backend
      // types before the FE switch catches up.
      return <DocumentArtifactViewer artifact={artifact} headingShift={headingShift} />;
  }
}
